import {connectAiInfographicStage,stageSavedAiInfographics} from './compat/ai-infographic-stage';
import {connectSourceSceneEditor,sourceSceneEditorBridge} from './host/source-scene-stage';
import {stageSavedSourceScene} from './compat/stage-source-scene';
import {connectSourceScriptSpeech} from './host/source-script-speech';
import {createSourceScriptSpeechActions} from './compat/source-script-speech-actions';
import {editorValueSignature} from '../protocol/native-envelope';
import {requireScene,sceneCaptions} from './compat/source-script-native';
import {getAudioData} from '../vendor/src/features/remotion-editor/editor/utils/get-audio-data';
import {requestHost} from './host/client';
import {AiSceneModifyTools} from './AiSceneModifyTools';
import {connectSceneVoiceStage} from './host/scene-voice';
import {readSceneVoiceDraft,type SceneVoiceDraft} from '../protocol/scene-voice';
import {FrameVideoTools} from './compat/FrameVideoTools';
import {GroupVoiceWindowControl} from './compat/GroupVoiceWindowControl';
import {ImageTools} from './compat/ImageTools';
import {MediaTools} from './MediaTools';
import {CaptionTranslationTools} from './CaptionTranslationTools';
import {AiSceneTools} from './AiSceneTools';
import {editorUserMessage} from '../protocol/user-message';
import '../protocol/editor-shell.css';
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Download, Ellipsis, PanelRightClose, SlidersHorizontal } from 'lucide-react';
import {NativeQaFingerprint} from './compat/NativeQaFingerprint';
import {WorkBackupRestore,type RestoreWorkBackup} from './compat/WorkBackupRestore';
import {handleWorkSaveShortcut} from './compat/work-save-shortcut';
import {connectVoiceDraftStage} from './host/voice-draft';
import {connectVideoDraftStage,connectVideoSourcePicker} from './host/video-draft';
import {connectVideoSourceUploadStage} from './host/video-source-upload';
import {readCompanyVideoDraft,type VideoDraftInput} from '../protocol/video-draft';
import {readCompanyVoiceDraft,type VoiceDraftInput} from '../protocol/voice-draft';
import type { PlayerRef } from "@remotion/player";
import {
  ContextProvider,
  PlayerRefContext,
} from "@/features/remotion-editor/editor/context-provider";
import {
  ProjectTypeProvider,
  useProjectTypeContext,
} from "@/features/remotion-editor/editor/contexts/project-type-context";
import { PropertyPanelProvider } from "@/features/remotion-editor/editor/contexts/property-panel-context";
import { AssetDownloadProgressProvider } from "@/features/remotion-editor/editor/contexts/asset-download-progress-context";
import { PreviewSizeProvider } from "@/features/remotion-editor/editor/preview-size-provider";
import { TopPanel } from "@/features/remotion-editor/editor/top-panel";
import { Timeline } from "@/features/remotion-editor/editor/timeline/timeline";
import { TimelineContainer } from "@/features/remotion-editor/editor/timeline/timeline-container";
import { TimelineToolbar } from "@/features/remotion-editor/editor/timeline-toolbar/timeline-toolbar";
import { TimelineResizer } from "@/features/remotion-editor/editor/timeline-resizer";
import EditorPropertyPanel from "@/features/remotion-editor/editor/editor-property-panel/EditorPropertyPanel";
import {
  useWriteContext,
  useCurrentStateAsRef,
  useFullState,
} from "@/features/remotion-editor/editor/utils/use-context";
import { hasPendingTimelineEdit } from "./compat/edit-ownership";
import {
  createNativeDocumentFile,
  type NativeFileState,
} from "./compat/native-document-file";
import type { SubscribeBrowserOutputAction,CompanyBrowserOutput,CompanySaveWork,CompanySaveIntent,CompanyEditorServices } from "../protocol/work-session";
import {readCompanyContinueClip,selectedCompanyContinueClip} from '../protocol/continue-clip';
import {readCompanySpeechClip,selectedCompanySpeechClip} from '../protocol/speech-clip';
import {readCompanyVoiceClip,selectedCompanyVoiceClip} from '../protocol/voice-replacement';
import type { NativeEditorEnvelope } from "../protocol/native-envelope";
import { DtoProjectType } from "@/shared/constants";
import { initializeWorkbenchProjectType } from './compat/effective-project-type';
import type { UndoableState } from "@/features/remotion-editor/editor/state/types";
import { ForceSpecificCursor } from "@/features/remotion-editor/editor/force-specific-cursor";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@crabs-ai/ds-react";

const fixture: UndoableState = {
  tracks: [
    {
      id: "fixture-video",
      items: ["fixture-left", "fixture-right"],
      hidden: false,
      muted: false,
      locked: false,
      magnetic: false,
      zone: "visual",
    },
  ],
  items: Object.fromEntries(
    ["fixture-left", "fixture-right"].map((id, index) => [
      id,
      {
        id,
        type: "solid",
        from: index * 90,
        durationInFrames: 90,
        left: 120,
        top: 180,
        width: 400,
        height: 400,
        rotation: 0,
        opacity: 1,
        isDraggingInTimeline: false,
        color: index ? "#e68941" : "#347cf2",
        borderRadius: 24,
        keepAspectRatio: false,
        fadeInDurationInSeconds: 0,
        fadeOutDurationInSeconds: 0,
      },
    ]),
  ),
  assets: {},
  transitions: {},
  fps: 30,
  compositionWidth: 720,
  compositionHeight: 1280,
  deletedAssets: [],
  backgroundColor: "#f8f6ef",
};
const captionFixture: UndoableState = {
  fps: 30,
  compositionWidth: 1080,
  compositionHeight: 1920,
  backgroundColor: "#FBF8F0",
  deletedAssets: [],
  transitions: {},
  tracks: [
    {
      id: "captions",
      items: ["source-caption"],
      hidden: false,
      muted: false,
      locked: false,
      magnetic: false,
      zone: "visual",
      metadata: { isMainTrack: true, type: "caption", trackType: null },
    },
  ],
  assets: {
    "caption-source": {
      id: "caption-source",
      type: "caption",
      filename: "source.json",
      size: 0,
      remoteUrl: null,
      remoteFileKey: null,
      mimeType: "application/json",
      captionLanguage: "ko",
      videoStart: 0,
      videoEnd: 6000,
      captions: [
        {
          text: "선크림만 바르면 화장이 밀리고",
          startMs: 0,
          endMs: 6000,
          timestampMs: 0,
          confidence: null,
        },
      ],
    },
  },
  items: {
    "source-caption": {
      id: "source-caption",
      type: "captions",
      assetId: "caption-source",
      from: 0,
      durationInFrames: 180,
      left: 0,
      top: 528,
      width: 1080,
      height: 93,
      opacity: 1,
      rotation: 0,
      isDraggingInTimeline: false,
      fontFamily: "Noto Sans KR",
      fontStyle: { variant: "normal", weight: "900" },
      fontSize: 63,
      lineHeight: 93 / 63,
      letterSpacing: -1.2,
      color: "#1A1A1A",
      highlightColor: "#1A1A1A",
      strokeWidth: 0,
      strokeColor: "#fff",
      align: "center",
      direction: "ltr",
      maxLines: 1,
      pageDurationInMilliseconds: 6000,
      captionStartInSeconds: 0,
      fadeInDurationInSeconds: 0,
      fadeOutDurationInSeconds: 0,
      sourceTypography: {
        preserveFontSize: true,
        verticalAlign: "top",
        whiteSpace: "pre-line",
        wrap: { mode: "em", maxEm: 17 },
        referenceWidth: 1080,
        referenceFontSize: 63,
      },
    },
  },
};
function InitializeFixture({
  sourceCaption,
  document,
  nativeFile,
  onReady,
}: {
  sourceCaption: boolean;
  document?: UndoableState;
  nativeFile?: NativeFileState;
  onReady: (ready: boolean) => void;
}) {
  const { setState } = useWriteContext();
  const { setProjectType } = useProjectTypeContext();
  const providerReady = useFullState().initialized;
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current || !providerReady) return;
    initialized.current = true;
    initializeWorkbenchProjectType(nativeFile?.settings.projectType, setProjectType);
    const { projectType: _projectType, ...settings } =
      nativeFile?.settings ?? {};
    setState({
      update: (state) => ({
        ...state,
        ...structuredClone(settings),
        initialized: true,
        selectedItems: document
          ? []
          : [sourceCaption ? "source-caption" : "fixture-left"],
        undoableState: structuredClone(
          document ?? (sourceCaption ? captionFixture : fixture),
        ),
      }),
      commitToUndoStack: false,
    });
    onReady(true);
  }, [
    setProjectType,
    setState,
    sourceCaption,
    document,
    nativeFile,
    providerReady,
    onReady,
  ]);
  return null;
}

function AuditSnapshotButton({
  playerRef,
  nativeFile,
  ready,
}: {
  playerRef: React.RefObject<PlayerRef | null>;
  nativeFile?: NativeFileState;
  ready: boolean;
}) {
  const state = useCurrentStateAsRef();
  const editing = hasPendingTimelineEdit(useFullState());
  return (
    <div className="flex shrink-0 items-center justify-end gap-3 bg-white">
      <button
        disabled={editing || !ready}
        className="shrink-0 px-md text-body-small"
        style={{
          background: "#fff",
          color: "#222",
          borderBottom: "1px solid #ddd",
          textAlign: "right",
        }}
        onClick={() => {
          const payload = {
            auditSchema: 1,
            frame: playerRef.current?.getCurrentFrame() ?? 0,
            document: state.current.undoableState,
          };
          const url = URL.createObjectURL(
            new Blob([JSON.stringify(payload, null, 2)], {
              type: "application/json",
            }),
          );
          const link = window.document.createElement("a");
          link.href = url;
          link.download =
            "company-editor-audit-" +
            new Date().toISOString().replace(/[:.]/g, "-") +
            ".json";
          link.click();
          window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        }}
      >
        검증 상태 내려받기 · 서버 저장 안 함
      </button>
      <NativeFileDownload nativeFile={nativeFile} ready={ready} />
    </div>
  );
}

function NativeFileDownload({
  nativeFile,
  ready,
}: {
  nativeFile?: NativeFileState;
  ready: boolean;
}) {
  const state = useCurrentStateAsRef();
  const editing = hasPendingTimelineEdit(useFullState());
  const { projectType } = useProjectTypeContext();
  const [message, setMessage] = useState("");
  const [backup,setBackup]=useState<{json:string;url:string;filename:string}|null>(null);
  useEffect(()=>()=>{if(backup)URL.revokeObjectURL(backup.url);},[backup]);
  return (
    <div className="px-2 py-1 text-sm">
      <button
        type="button"
        disabled={editing || !ready}
        onClick={() => {
          try {
            const envelope = createNativeDocumentFile(state.current, {
              savedAt: new Date().toISOString(),
              projectType,
              previous: nativeFile?.envelope,
            });
            const json=JSON.stringify(envelope,null,2);
            const url = URL.createObjectURL(
              new Blob([json], {
                type: "application/json",
              }),
            );
            const link = window.document.createElement("a");
            link.href = url;
            const filename =
              "waka-editor-" +
              new Date().toISOString().replace(/[:.]/g, "-") +
              ".json";
            link.download = filename;
            setBackup({json,url,filename});
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            setMessage(
              "내려받기 요청: " + filename + " · 서버에는 저장하지 않았습니다.",
            );
          } catch (error) {
            setMessage(
              error instanceof Error
                ? error.message
                : "편집 파일을 만들지 못했습니다.",
            );
          }
        }}
      >
        편집 파일 내려받기
      </button>
      {backup&&<span className="ml-3 inline-flex gap-3">
        <a href={backup.url} download={backup.filename}>생성한 편집 파일 저장</a>
        <button type="button" onClick={async()=>{
          try{await navigator.clipboard.writeText(backup.json);setMessage('생성한 편집 파일 내용을 복사했습니다. 이후 변경 내용은 새로 내보내 주세요. 서버에는 저장하지 않았습니다.');}
          catch{setMessage('복사를 완료하지 못했습니다. 생성한 편집 파일 저장 링크를 이용해 주세요.');}
        }}>생성한 편집 파일 내용 복사</button>
      </span>}
      {backup&&<details className="mt-2"><summary>편집 파일 내용 펼치기</summary>
        <textarea aria-label="생성한 편집 백업 내용" readOnly value={backup.json} rows={5} className="mt-2 block w-full rounded border p-2 font-mono text-xs"/>
      </details>}
      {message && <p role="status">{message}</p>}
    </div>
  );
}

const ALL_SERVICES:CompanyEditorServices={captionTranslation:true,aiScene:true,aiSceneModify:true};

/** Both the button and keyboard shortcut persist through the first-party work transport. */
function WorkSaveButton({
  ready,
  nativeFile,
  saveWork,
  publishBrowserOutput,
  subscribeBrowserOutputAction,
  utilities,
  services=ALL_SERVICES,
}: {
  utilities?:ReactNode;
  services?:CompanyEditorServices;
  ready: boolean;
  nativeFile?: NativeFileState;
  saveWork: CompanySaveWork;
  publishBrowserOutput?:CompanyBrowserOutput;
  subscribeBrowserOutputAction?:SubscribeBrowserOutputAction;
}) {
  const state = useFullState();
  const current=useCurrentStateAsRef();
  const { projectType } = useProjectTypeContext();
  const [saving, setSaving] = useState(false);
  const saveButton = useRef<HTMLButtonElement>(null);
  const toolTrigger = useRef<HTMLButtonElement>(null);
  const fileMenu = useRef<HTMLDetailsElement>(null);
  const drawer = useRef<HTMLElement>(null);
  const drawerBody = useRef<HTMLDivElement>(null);
  const drawerClose = useRef<HTMLButtonElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [category, setCategory] = useState<'scene'|'speech'|'ai'>('scene');
  // Hide tools whose host service is absent instead of letting them fail after a click.
  const aiTools=services.aiScene||services.aiSceneModify;
  const toolTabs=([['scene','장면·이미지'],['speech','음성·자막'],['ai','AI']] as const).filter(([id])=>id!=='ai'||aiTools);
  const [outputOpen, setOutputOpen] = useState(false);
  const saveInFlight = useRef(false);
  const outputAction=useRef<(action:'render'|'publish')=>void>(()=>{});
  useEffect(()=>{outputAction.current=action=>{if(action==='render')void performSave('browser-render');else if(!output)setMessage('먼저 MP4를 출력해 주세요.');else void publish();};});
  useEffect(()=>subscribeBrowserOutputAction?.(action=>outputAction.current(action)),[subscribeBrowserOutputAction]);
  const renderAbort=useRef<AbortController|null>(null);
  const [rendering,setRendering]=useState(false);
  const [progress,setProgress]=useState(0);
  const [output,setOutput]=useState<{blob:Blob;url:string;revision:string}|null>(null);
  const [publishing,setPublishing]=useState(false);
  useEffect(()=>()=>renderAbort.current?.abort(),[]);
  useEffect(()=>()=>{if(output)URL.revokeObjectURL(output.url);},[output]);
  async function publish(){
    if(!output||!publishBrowserOutput||publishing)return;
    setPublishing(true);
    try{setMessage(await publishBrowserOutput(output));}
    catch(error){setMessage(editorUserMessage(error,'갤러리에 보관하지 못했습니다. 다시 시도해 주세요.')+' MP4 내려받기는 계속 사용할 수 있습니다.');}
    finally{setPublishing(false);}
  }
  useEffect(() => {
    const keydown = (event: KeyboardEvent) => handleWorkSaveShortcut(event, saveButton.current);
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, []);
  useEffect(() => {
    if (!drawerOpen) return;
    if (fileMenu.current) fileMenu.current.open = false;
    drawerClose.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (document.querySelector('[role="dialog"][data-state="open"]') || (event.target instanceof Element && event.target.closest('[role="dialog"]'))) return;
        event.preventDefault();
        setDrawerOpen(false);
        toolTrigger.current?.focus();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[role="dialog"]')) return;
      if (target instanceof Node && !drawer.current?.contains(target) && !toolTrigger.current?.contains(target)) setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [drawerOpen]);
  useEffect(() => {
    const closeFiles = (event: KeyboardEvent | PointerEvent) => {
      const menu = fileMenu.current;
      if (!menu?.open || (event.target instanceof Element && event.target.closest('[role="dialog"]'))) return;
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        menu.open = false;
        menu.querySelector('summary')?.focus();
      } else if (event.target instanceof Node && !menu.contains(event.target)) menu.open = false;
    };
    document.addEventListener('keydown', closeFiles);
    document.addEventListener('pointerdown', closeFiles);
    return () => {
      document.removeEventListener('keydown', closeFiles);
      document.removeEventListener('pointerdown', closeFiles);
    };
  }, []);
  const [hasSaved, setHasSaved] = useState(!nativeFile?.requiresInitialSave);
  const [message, setMessage] = useState("");
  const [baseline, setBaseline] = useState<typeof state.undoableState | null>(
    null,
  );
  const previous = useRef(nativeFile?.envelope);
  const savedRevision=useRef(nativeFile?.revision);
  const [revisionVersion,setRevisionVersion]=useState(0);
  const settingsKey = JSON.stringify([
    state.isLinkingEnabled,
    state.isSnappingEnabled,
    state.isSkimmingEnabled,
    state.loop,
    state.brandTemplateId,
    state.editorAppliedAccountBrandTemplateId,
    state.safeZoneGuide,
    projectType,
  ]);
  const [baselineSettings, setBaselineSettings] = useState<string | null>(null);
  useEffect(() => {
    if (ready && baseline === null) {
      setBaseline(state.undoableState);
      setBaselineSettings(settingsKey);
    }
  }, [ready, baseline, settingsKey, state.undoableState]);
  const dirty =
    ready &&
    (baseline !== state.undoableState || baselineSettings !== settingsKey);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty || saving || !hasSaved) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, saving, hasSaved]);
  const continueClipId=selectedCompanyContinueClip(state.undoableState,state.selectedItems);
  const speechClipId=selectedCompanySpeechClip(state.undoableState,state.selectedItems);
  const voiceClipId=selectedCompanyVoiceClip(state.undoableState,state.selectedItems,state.inspectedGroupMemberId);
  const stageRef=useRef<(draft:VoiceDraftInput)=>Promise<void>>(async()=>{});
  useEffect(()=>{stageRef.current=async draft=>{if(!await performSave({type:'voice-draft',draft}))throw new Error('음성 작성 내용을 저장하지 못했습니다. 상단 저장 안내를 확인해 주세요.');};});
  useEffect(()=>connectVoiceDraftStage(draft=>stageRef.current(draft)),[]);
  const sceneVoiceStageRef=useRef<(draft:SceneVoiceDraft)=>Promise<void>>(async()=>{});
  useEffect(()=>{sceneVoiceStageRef.current=async draft=>{if(!await performSave({type:'scene-voice',draft}))throw Error('장면 음성 요청을 저장하지 못했습니다. 상단 안내를 확인해 주세요.');};});
  useEffect(()=>connectSceneVoiceStage(draft=>sceneVoiceStageRef.current(draft)),[]);
  const sourceSpeechRef=useRef<Parameters<typeof connectSourceScriptSpeech>[0]|null>(null);
  useEffect(()=>{
    const get=()=>current.current;
    sourceSpeechRef.current={actions:createSourceScriptSpeechActions({getState:get,save:()=>performSave(),revision:()=>savedRevision.current,
      transcribe:(assetId,revision)=>requestHost({method:'POST',path:'/__waka/transcribe',body:{assetId,revision}}),pcm:getAudioData,
      template:()=>{const d=get().undoableState;for(const track of d.tracks)for(const id of track.items){const item=d.items[id];if(item?.type==='captions'&&!track.locked)return {item,trackId:track.id};}return undefined;}}),
      stage:async(target,voice,fallback)=>{
        const d=get().undoableState;if(editorValueSignature(d)!==target.signature)throw Error('문서가 바뀌었습니다. 음성 대본을 다시 확인해 주세요.');
        const scene=requireScene(d,target.sceneKey),captionIds=sceneCaptions(d,scene).map(c=>c.item.id);
        const {draft}=readSceneVoiceDraft({selection:{sceneKey:scene.key,captionIds},text:voice.text,settings:fallback},d);
        if(!await performSave({type:'scene-voice',draft}))throw Error('나레이션 저장과 음성 준비를 마치지 못했습니다. 상단 안내를 확인해 주세요.');
      }};
  });
  useEffect(()=>connectSourceScriptSpeech({actions:{autoCaption:(...args)=>sourceSpeechRef.current!.actions.autoCaption!(...args),resync:(...args)=>sourceSpeechRef.current!.actions.resync!(...args),silence:(...args)=>sourceSpeechRef.current!.actions.silence!(...args),restore:(...args)=>sourceSpeechRef.current!.actions.restore!(...args)},stage:(...args)=>sourceSpeechRef.current!.stage(...args)}),[]);
  const videoStageRef=useRef<(draft:VideoDraftInput)=>Promise<void>>(async()=>{});
  useEffect(()=>{videoStageRef.current=async draft=>{if(!await performSave({type:'video-draft',draft}))throw new Error('영상 요청을 저장하지 못했습니다. 상단 안내를 확인해 주세요.');};});
  useEffect(()=>connectVideoDraftStage(draft=>videoStageRef.current(draft)),[]);
  const videoPickerRef=useRef<(kind:'upload'|'library')=>Promise<void>>(async()=>{});
  useEffect(()=>{videoPickerRef.current=async kind=>{if(!await performSave({type:'video-source',kind}))throw new Error('원본 선택 창을 열지 못했습니다. 상단 안내를 확인해 주세요.');};});
  useEffect(()=>connectVideoSourcePicker(kind=>videoPickerRef.current(kind)),[]);
  const videoUploadRef=useRef<()=>Promise<void>>(async()=>{throw new Error('편집기를 준비 중입니다.');});
  useEffect(()=>{videoUploadRef.current=async()=>{if(!await performSave())throw new Error('원본 추가 전에 현재 편집본을 저장하지 못했습니다.');};});
  useEffect(()=>connectVideoSourceUploadStage(()=>videoUploadRef.current()),[]);
  const sourceSceneSave=useRef(performSave);sourceSceneSave.current=performSave;
  useEffect(()=>{
    const scope=previous.current?.scope,revision=savedRevision.current;
    if(!scope||!revision||!ready)return;
    return connectSourceSceneEditor({scope,revision,stage:(intent,mode)=>stageSavedSourceScene(intent,mode,{scope,revision,getState:()=>current.current,save:()=>sourceSceneSave.current(),saved:()=>previous.current&&savedRevision.current?{file:previous.current,revision:savedRevision.current}:null,requestId:()=>crypto.randomUUID(),host:requestHost}).then(()=>{})});
  },[ready,revisionVersion]);
  useEffect(()=>connectAiInfographicStage(brief=>stageSavedAiInfographics(brief,{state:()=>current.current,save:()=>sourceSceneSave.current(),saved:()=>previous.current&&savedRevision.current?{file:previous.current,revision:savedRevision.current}:null,host:requestHost,id:()=>crypto.randomUUID()})),[]);
  async function performSave(intent?:CompanySaveIntent){
    if(saveInFlight.current||renderAbort.current||!ready||hasPendingTimelineEdit(state))return false;
    saveInFlight.current=true;setSaving(true);
    try{
      const document=state.undoableState,settings=settingsKey;
      const file=createNativeDocumentFile(state,{savedAt:new Date().toISOString(),projectType,previous:previous.current});
      if(intent&&typeof intent==='object'){
        if(intent.type==='voice-draft')readCompanyVoiceDraft(intent.draft,file.document);
        else if(intent.type==='scene-voice')readSceneVoiceDraft(intent.draft,file.document);
        else if(intent.type==='video-draft')readCompanyVideoDraft(intent.draft,file.document);
        else if(intent.type==='video-source'){if(intent.kind!=='upload'&&intent.kind!=='library')throw new Error('원본 선택 경로를 확인해 주세요.');}
        else if(intent.type==='continue')readCompanyContinueClip(file.document,intent.clipId);
        else if(intent.type==='voice')readCompanyVoiceClip(file.document,intent.clipId);
        else readCompanySpeechClip(file.document,intent.clipId);
      }
      const result=await saveWork(file,intent);
      if(!result.ok){setMessage(editorUserMessage(result.message));return false;}
      previous.current=result.file;savedRevision.current=result.revision;setRevisionVersion(v=>v+1);setHasSaved(true);setBaseline(document);setBaselineSettings(settings);
      setMessage(result.output?.message??'와카 작업에 저장했습니다. 다시 열어 이어서 편집할 수 있습니다.');
      if(intent==='browser-render'&&result.output?.browser){
        const controller=new AbortController();renderAbort.current=controller;setRendering(true);setProgress(0);
        setOutputOpen(true);setDrawerOpen(true);
        if(drawerBody.current)drawerBody.current.scrollTop=0;
        try{
          const {renderBrowserMp4}=await import('./render/browser-render');
          const blob=await renderBrowserMp4(result.file,{...result.output.browser,signal:controller.signal,onProgress:setProgress});
          setOutput({blob,url:URL.createObjectURL(blob),revision:result.revision});
          setOutputOpen(true);
          setDrawerOpen(true);
          setMessage(`${blob.type==='video/webm'?'WebM':'MP4'} 출력 완료. 내려받거나 갤러리에 보관할 수 있습니다.`);
        }catch(error){if(import.meta.env.DEV)console.error('[browser-export]',error);setMessage(controller.signal.aborted?'출력을 취소했습니다. 저장본은 유지됩니다.':editorUserMessage(error,'영상을 출력하지 못했습니다. 원본 연결을 확인한 뒤 다시 시도해 주세요.')+' 저장본은 유지됩니다. 수정 후 다시 출력해 주세요.');}
        finally{renderAbort.current=null;setRendering(false);}
      }
      return result.output?.ok!==false;
    }catch(error){setMessage(editorUserMessage(error,'저장하지 못했습니다. 연결을 확인한 뒤 다시 시도해 주세요.'));return false;}
    finally{saveInFlight.current=false;setSaving(false);}
  }
  return (
    <>
    <div className="precision-workbar">
      {window.parent===window&&<h1 className="precision-workbar-title">정밀 편집</h1>}
      <button ref={toolTrigger} className="precision-tool-trigger" type="button" aria-label="편집 도구" aria-expanded={drawerOpen} aria-controls="precision-tool-drawer" onClick={()=>setDrawerOpen(open=>!open)}>
        <SlidersHorizontal size={16} aria-hidden="true"/><span>편집 도구</span>
      </button>
      <span className="precision-save-status" role="status">{dirty ? "저장하지 않은 변경 있음" : hasSaved ? "현재 저장본" : "저장 전"}</span>
      <button
        ref={saveButton}
        className="precision-save-action"
        type="button"
        aria-label="와카 작업에 저장"
        aria-keyshortcuts="Control+s Meta+s"
        title="와카 작업에 저장 (Ctrl/⌘+S · 입력 중에는 먼저 입력을 마쳐 주세요)"
        disabled={!ready || saving || hasPendingTimelineEdit(state)}
        onClick={()=>void performSave()}
      >
        {saving ? "저장 중…" : "저장"}
      </button>
      {window.parent===window&&<button className="precision-primary precision-export-action" type="button" aria-label="저장 후 MP4 출력" disabled={!ready||saving||hasPendingTimelineEdit(state)} onClick={()=>void performSave('browser-render')}><Download size={16} aria-hidden="true"/><span>내보내기</span></button>}
      <FrameVideoTools disabled={!ready||saving||rendering}/>
      {utilities&&<div className="precision-utilities"><details ref={fileMenu} onToggle={event=>{if(event.target===event.currentTarget&&event.currentTarget.open)setDrawerOpen(false);}}><summary aria-label="더 보기"><Ellipsis size={18} aria-hidden="true"/><span>더 보기</span></summary><div>{utilities}</div></details></div>}
      <aside ref={drawer} id="precision-tool-drawer" className="precision-tool-drawer" aria-label="편집 도구" hidden={!drawerOpen}>
        <div className="precision-tool-drawer-heading"><h2>편집 도구</h2><button ref={drawerClose} type="button" aria-label="편집 도구 닫기" onClick={()=>{setDrawerOpen(false);toolTrigger.current?.focus();}}><PanelRightClose size={18} aria-hidden="true"/></button></div>
        <div className="precision-tool-tabs" role="tablist" aria-label="도구 분류" onKeyDown={event=>{
          const keys=toolTabs.map(([id])=>id);
          const index=keys.indexOf(category);
          const next=event.key==='ArrowRight'?keys[(index+1)%keys.length]:event.key==='ArrowLeft'?keys[(index+keys.length-1)%keys.length]:event.key==='Home'?keys[0]:event.key==='End'?keys[keys.length-1]:null;
          if(next){event.preventDefault();setCategory(next);document.getElementById(`precision-tab-${next}`)?.focus();}
        }}>
          {toolTabs.map(([id,label])=><button key={id} id={`precision-tab-${id}`} type="button" role="tab" aria-selected={category===id} aria-controls={`precision-panel-${id}`} tabIndex={category===id?0:-1} onClick={()=>setCategory(id)}>{label}</button>)}
        </div>
        <div ref={drawerBody} className="precision-tool-drawer-body">
          {(rendering||output)&&<details className="precision-output-section" open={outputOpen} onToggle={event=>setOutputOpen(event.currentTarget.open)}><summary>출력 영상 확인 · {rendering?`${Math.round(progress*100)}%`:'완료'}</summary><div>
            {rendering&&<><span role="status">브라우저 출력 {Math.round(progress*100)}% · 탭을 열어두세요</span><button type="button" onClick={()=>renderAbort.current?.abort()}>출력 취소</button></>}
            {output&&<><video controls src={output.url}/><a href={output.url} download={`crabs-precision.${output.blob.type==='video/webm'?'webm':'mp4'}`}>{output.blob.type==='video/webm'?'WebM':'MP4'} 내려받기</a>{publishBrowserOutput&&<button type="button" disabled={publishing} onClick={()=>void publish()}>{publishing?'갤러리 저장 중…':'출력본 갤러리 보관'}</button>}</>}
          </div></details>}
          <section id="precision-panel-scene" className="precision-tool-section" role="tabpanel" aria-labelledby="precision-tab-scene" hidden={category!=='scene'}>
            <p className="precision-tool-hint">타임라인에서 이미지나 영상 컷을 선택해 주세요.</p>
            <button type="button" aria-label="선택한 컷 AI 이어 만들기" title={continueClipId?'선택한 컷 바로 뒤에 이어질 영상을 만듭니다. 생성 전에 설정과 비용을 확인합니다.':'타임라인에서 영상 또는 이미지 컷 하나를 선택해 주세요.'} disabled={!ready||saving||rendering||hasPendingTimelineEdit(state)||!continueClipId} onClick={()=>{if(continueClipId)void performSave({type:'continue',clipId:continueClipId});}}>컷 뒤에 AI 이어 만들기</button>
            <ImageTools disabled={!ready||saving||rendering} save={()=>performSave()} revision={()=>savedRevision.current}/>
          </section>
          <section id="precision-panel-speech" className="precision-tool-section" role="tabpanel" aria-labelledby="precision-tab-speech" hidden={category!=='speech'}>
            <MediaTools disabled={!ready||saving||rendering} save={()=>performSave()} revision={()=>savedRevision.current}/>
            <details className="precision-tools"><summary>음성 생성·교체</summary><div>
              <GroupVoiceWindowControl clipId={voiceClipId} disabled={!ready||saving||rendering||hasPendingTimelineEdit(state)}/>
              <button type="button" disabled={!ready||saving||hasPendingTimelineEdit(state)||!speechClipId} onClick={()=>{if(speechClipId)void performSave({type:'speech',clipId:speechClipId});}}>선택 자막 음성 추가</button>
              <button type="button" disabled={!ready||saving||hasPendingTimelineEdit(state)||!voiceClipId} onClick={()=>{if(voiceClipId)void performSave({type:'voice',clipId:voiceClipId});}}>선택 오디오를 음성으로 교체</button>
            </div></details>
            {services.captionTranslation&&nativeFile?.envelope.scope&&<CaptionTranslationTools key={`translation:${JSON.stringify(nativeFile.envelope.scope)}`} scope={nativeFile.envelope.scope} disabled={!ready||saving||rendering} save={()=>performSave()} revision={()=>savedRevision.current}/>}
          </section>
          {aiTools&&<section id="precision-panel-ai" className="precision-tool-section" role="tabpanel" aria-labelledby="precision-tab-ai" hidden={category!=='ai'}>
            {services.aiScene&&nativeFile?.envelope.scope&&<AiSceneTools key={`generate:${JSON.stringify(nativeFile.envelope.scope)}`} scope={nativeFile.envelope.scope} disabled={!ready||saving||rendering} save={()=>performSave()} revision={()=>savedRevision.current}/>}
            {services.aiSceneModify&&nativeFile?.envelope.scope&&<AiSceneModifyTools key={`modify:${JSON.stringify(nativeFile.envelope.scope)}`} scope={nativeFile.envelope.scope} disabled={!ready||saving||rendering} save={()=>performSave()} revision={()=>savedRevision.current}/>}
          </section>}
          {drawerOpen&&message&&<p className="precision-workbar-message" role="status">{message}</p>}
        </div>
      </aside>
    </div>
    {!drawerOpen&&message&&<p className="precision-workbar-strip" role="status">{message}</p>}
    </>
  );
}

export function EngineWorkbench({
  sourceCaption = false,
  document: providedDocument,
  nativeFile,
  saveWork,
  publishBrowserOutput,
  subscribeBrowserOutputAction,
  subscribeUploadedMedia,
  onRestore,
  restoreRecovery,
  restoreBusy=false,
  services,
}: {
  sourceCaption?: boolean;
  document?: UndoableState;
  nativeFile?: NativeFileState;
  saveWork?: CompanySaveWork;
  publishBrowserOutput?:CompanyBrowserOutput;
  subscribeBrowserOutputAction?:SubscribeBrowserOutputAction;
  subscribeUploadedMedia?: SubscribeUploadedMedia;
  onRestore?: RestoreWorkBackup;
  restoreRecovery?: NativeFileState;
  restoreBusy?: boolean;
  services?: CompanyEditorServices;
}) {
  const playerRef = useRef<PlayerRef | null>(null);
  const [ready, setReady] = useState(false);
  const document = nativeFile?.document ?? providedDocument;
  const utilities=ready?<details><summary>백업 및 복원</summary><div><NativeFileDownload nativeFile={nativeFile} ready={ready}/>{nativeFile&&onRestore&&<WorkBackupRestore nativeFile={nativeFile} onRestore={onRestore} recovery={restoreRecovery} busy={restoreBusy}/>}</div></details>:null;
  return (
    <div className="flex h-screen w-screen flex-col bg-editor-starter-bg">
      {window.parent===window&&!saveWork&&<header className="precision-editor-title">정밀 편집</header>}
      {nativeFile?.importNotice&&<p className="precision-inline-note">{nativeFile.importNotice}</p>}
      <ContextProvider>
        <PlayerRefContext.Provider value={playerRef}>
          <ProjectTypeProvider>
            <PropertyPanelProvider>
              <AssetDownloadProgressProvider>
                <InitializeFixture
                  sourceCaption={sourceCaption}
                  document={document}
                  nativeFile={nativeFile}
                  onReady={setReady}
                />
                {import.meta.env.DEV&&new URLSearchParams(window.location.search).get('diagnostics')==='1'&&<AuditSnapshotButton playerRef={playerRef} nativeFile={nativeFile} ready={ready}/>}
                {!saveWork&&utilities&&<div className="precision-utilities">{utilities}</div>}
                {ready && subscribeUploadedMedia && <ReceiveUploadedMedia subscribe={subscribeUploadedMedia} playerRef={playerRef} authority={()=>{const scope=sourceSceneEditorBridge()?.scope,revision=sourceSceneEditorBridge()?.revision;if(!scope||!revision)throw Error('작업 저장 정보를 확인해 주세요.');return {scope,revision};}}/>}
                {import.meta.env.DEV && new URLSearchParams(window.location.search).get('diagnostics')==='1' && ready && <NativeQaFingerprint nativeFile={nativeFile}/>}
                {saveWork && (
                  <WorkSaveButton utilities={utilities}
                    ready={ready}
                    nativeFile={nativeFile}
                    saveWork={saveWork}
                    publishBrowserOutput={publishBrowserOutput}
                    subscribeBrowserOutputAction={subscribeBrowserOutputAction}
                    services={services}
                  />
                )}
                <PreviewSizeProvider>
                  <ResizablePanelGroup
                    direction="horizontal"
                    className="flex min-h-0 flex-1"
                  >
                    <ResizablePanel
                      defaultSize={30}
                      minSize={20}
                      maxSize={45}
                      className="h-full"
                    >
                      <div id="editor-property-panel" className="h-full">
                        <EditorPropertyPanel
                          playerRef={playerRef}
                          projectId={0}
                        />
                      </div>
                    </ResizablePanel>
                    <ResizableHandle className="w-[1px] cursor-col-resize bg-sys-style-neutral-default transition-colors hover:bg-sys-style-primary-default active:bg-sys-style-primary-default" />
                    <ResizablePanel defaultSize={70} className="h-full">
                      <TopPanel playerRef={playerRef} />
                    </ResizablePanel>
                  </ResizablePanelGroup>
                </PreviewSizeProvider>
                <TimelineResizer />
                <TimelineToolbar playerRef={playerRef} />
                <TimelineContainer playerRef={playerRef}>
                  <Timeline playerRef={playerRef} />
                </TimelineContainer>
                <ForceSpecificCursor />
              </AssetDownloadProgressProvider>
            </PropertyPanelProvider>
          </ProjectTypeProvider>
        </PlayerRefContext.Provider>
      </ContextProvider>
    </div>
  );
}
import {ReceiveUploadedMedia} from './compat/ReceiveUploadedMedia';
import type {SubscribeUploadedMedia} from '../protocol/work-session';
